import { useKeyValue, useKeys as useKeysQuery, useSearch as useSearchQuery } from '../services/api/cloudflare/keys';
import { useState } from 'react';
import { useZeValueStore } from '../store/namespace';
import { useKeyValueWithCache } from './useKeysWithCache';
import { useLocalSearch } from './useLocalSearch';

// Smart hook for key selection and management
export function useKeySelection(namespaceId: string) {
  const [selectedKey, setSelectedKey] = useState<string | null>(null);
  // Use cached key value instead of direct API call
  const keyValueQuery = useKeyValueWithCache(namespaceId, selectedKey || '');

  const handleKeySelect = (keyName: string) => {
    setSelectedKey(keyName);
  };

  const clearSelection = () => {
    setSelectedKey(null);
  };

  return {
    selectedKey,
    keyValueQuery,
    handleKeySelect,
    clearSelection,
    isLoading: keyValueQuery.isLoading,
    data: keyValueQuery.data,
    isCached: keyValueQuery.isCached,
    refreshFromNetwork: keyValueQuery.refreshFromNetwork
  };
}

// Re-export the base query hook with cache support
export function useKeys(namespaceId: string, options = {}) {
  // Old implementation used direct API query
  // return useKeysQuery(namespaceId, options);
  // We'll now use this hook to support backward compatibility
  return useKeysWithCache(namespaceId, options);
}

// Hook for searching across namespaces with IndexedDB cache
export function useSearch(searchTerm: string, options: {
  includeValues?: boolean;
  limit?: number;
  enabled?: boolean;
  exactKey?: boolean;
} = {}) {
  // Determine if we should use exactKey search (if it looks like a full key name)
  const shouldUseExactKeySearch = !options.exactKey && searchTerm.includes(':') && !searchTerm.includes('*') && !searchTerm.includes('?');
  
  const searchOptions = {
    ...options,
    exactKey: options.exactKey || shouldUseExactKeySearch
  };
  
  // Use local search first, then fall back to API search if needed
  const localSearchQuery = useLocalSearch(searchTerm, searchOptions);
  const apiSearchQuery = useSearchQuery(searchTerm, {
    ...searchOptions,
    // Only enable API search if local search returned no results and we're not already loading
    enabled: searchOptions.enabled && 
             searchTerm.length >= 2 && 
             !localSearchQuery.isLoading && 
             localSearchQuery.data.results.length === 0
  });
  
  // Combine results with preference for local search
  const isLoading = localSearchQuery.isLoading || apiSearchQuery.isLoading;
  const results = localSearchQuery.data.results.length > 0 
    ? localSearchQuery.data.results 
    : (apiSearchQuery.data?.results || []);
  
  const { setNamespace, setKey, setZeValue } = useZeValueStore();

  const handleSelectSearchResult = (result: {
    key: string;
    namespaceId: string;
    namespaceName?: string;
    value?: any;
  }) => {
    const namespaceName = result.namespaceName || result.namespaceId;
    
    if (namespaceName === 'ze_envs') {
      setNamespace('ze_envs');
      setKey(result.key);
      if (result.value) {
        setZeValue(result.value);
      }
    } else if (namespaceName === 'ze_snapshots') {
      setNamespace('ze_snapshots');
      setKey(result.key);
      if (result.value) {
        setZeValue(result.value);
      }
    } else if (namespaceName === 'ze_files') {
      setNamespace('ze_files');
      setKey(result.key);
      if (result.value) {
        setZeValue(result.value);
      }
    } else {
      // For any other namespaces
      setNamespace(result.namespaceId);
      setKey(result.key);
      if (result.value) {
        setZeValue(result.value);
      }
    }
  };

  return {
    data: { results },
    isLoading,
    isError: localSearchQuery.isError || apiSearchQuery.isError,
    error: localSearchQuery.error || apiSearchQuery.error,
    handleSelectSearchResult,
    refetch: localSearchQuery.refetch
  };
}